import { useCallback, useState } from 'react';
import { Send, Mail, CheckCircle, Loader2 } from 'lucide-react';
import { contactService } from '../lib/api-service';
import { HCaptcha } from '../components/HCaptcha';
import { DocumentMeta } from '../hooks/useDocumentMeta';

// hCaptcha publishes a "always-pass" public test sitekey for development.
const HCAPTCHA_TEST_SITEKEY = '10000000-ffff-ffff-ffff-000000000001';
const HCAPTCHA_SITEKEY = import.meta.env.VITE_HCAPTCHA_SITEKEY || HCAPTCHA_TEST_SITEKEY;

const CONTACT_EMAIL = 'contact.ctro@albaiulia2026.ro';

export default function Contact() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleVerify = useCallback((token: string) => {
    setCaptchaToken(token);
  }, []);

  const handleExpire = useCallback(() => {
    setCaptchaToken('');
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!captchaToken) {
      setError('Te rugăm să confirmi că nu ești robot.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await contactService.send({ name, email, subject, message, captcha_token: captchaToken });
      setDone(true);
    } catch {
      setError('A apărut o eroare la trimitere. Reîncearcă în câteva minute.');
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="mx-auto max-w-xl px-4 py-16 text-center" style={{ fontFamily: 'var(--font-secondary)' }}>
        <DocumentMeta title="Contact" url="/contact" />
        <CheckCircle className="mx-auto mb-4 h-16 w-16 text-green-500" />
        <h1 className="mb-2 text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
          Mesajul a fost trimis
        </h1>
        <p className="text-muted-foreground">
          Mulțumim! Revenim cu răspunsul cât de curând.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8" style={{ fontFamily: 'var(--font-secondary)' }}>
      <DocumentMeta
        title="Contact"
        description="Scrie-ne — formular de contact pentru programul Capitala Tineretului din România 2026."
        url="/contact"
      />
      <h1 className="mb-2 text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
        Contact
      </h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Ai o întrebare, o propunere de eveniment sau vrei să devii partener? Scrie-ne.
      </p>

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <form onSubmit={handleSubmit} className="space-y-4 rounded-2xl border bg-white p-5">
          <div>
            <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>Nume</label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>Subiect</label>
            <input
              type="text"
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium" style={{ fontFamily: 'var(--font-primary)' }}>Mesaj</label>
            <textarea
              required
              rows={6}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={5000}
              className="w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:border-[var(--vivid-purple)] focus:ring-2 focus:ring-[var(--vivid-purple)]/20"
            />
          </div>

          <div>
            <HCaptcha sitekey={HCAPTCHA_SITEKEY} onVerify={handleVerify} onExpire={handleExpire} />
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="flex items-center justify-center gap-2 rounded-lg bg-[var(--vivid-purple)] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-purple)]/90 disabled:opacity-60"
            style={{ fontFamily: 'var(--font-primary)' }}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {submitting ? 'Se trimite...' : 'Trimite mesajul'}
          </button>
        </form>

        <aside className="space-y-4">
          <div className="rounded-2xl border bg-white p-5">
            <h2 className="mb-2 text-sm font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
              Sau scrie-ne direct
            </h2>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="flex items-center gap-2 text-sm text-[var(--vivid-purple)] hover:underline"
            >
              <Mail className="h-4 w-4" />
              {CONTACT_EMAIL}
            </a>
          </div>
          <div className="rounded-2xl border bg-[var(--vivid-yellow-green)]/10 p-5">
            <p className="text-xs leading-relaxed text-muted-foreground">
              Răspundem de obicei în 1–2 zile lucrătoare. Pentru solicitări urgente legate
              de un eveniment în curs, ia legătura direct cu organizatorul evenimentului.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
